import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft,
  RefreshCw,
  BarChart3,
  Cog,
  CheckCircle,
  Workflow,
  GitBranch,
  Settings,
  BookOpen,
  FileText,
  Download,
  Eye,
  AlertTriangle,
  Play
} from 'lucide-react';

export const CanonEngineGuide: React.FC = () => {
  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/elevation/canon-engine" 
            className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Canon Engine
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-consulting-slate-blue-100 flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-consulting-slate-blue-600" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-charcoal-900">Canon Engine User Guide</h1>
              <p className="text-charcoal-600 mt-1">How to manage and operate the Canon Engine dashboard</p>
            </div>
          </div>
        </div>

        {/* Overview */}
        <section className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Overview</h2>
          <p className="text-charcoal-700 leading-relaxed mb-4">
            The Canon Engine dashboard provides a visual interface for managing your canonical documentation system. 
            This guide explains what each button, tab, and feature does so you can effectively manage your documentation.
          </p>
        </section>

        {/* Refresh Button */}
        <section className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <RefreshCw className="h-6 w-6 text-consulting-slate-blue-600" />
            <h2 className="text-2xl font-bold text-charcoal-900">Refresh Button</h2>
          </div>
          <div className="space-y-4">
            <div className="p-4 bg-consulting-slate-blue-50 border-l-4 border-consulting-slate-blue-500">
              <h3 className="font-semibold text-charcoal-900 mb-2">What It Does</h3>
              <p className="text-charcoal-700">
                The Refresh button (top right, with circular arrow icon) reloads all data from the Canon Engine API:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-charcoal-700">
                <li><strong>Status:</strong> Current engine status, section counts, validation results</li>
                <li><strong>Sections:</strong> List of all canonical sections</li>
                <li><strong>Errors:</strong> Any validation errors or warnings</li>
                <li><strong>Changelog:</strong> Latest changelog from staging synthesis</li>
              </ul>
            </div>
            <div className="p-4 bg-[var(--mustard-dim)] border-l-4 border-[var(--mustard)]">
              <h3 className="font-semibold text-charcoal-900 mb-2">When to Use It</h3>
              <ul className="list-disc list-inside space-y-1 text-charcoal-700">
                <li>After running a staging synthesis operation</li>
                <li>After promoting staging to production</li>
                <li>If you suspect the displayed data is stale</li>
                <li>After making changes via the CLI or API</li>
              </ul>
            </div>
            <div className="p-4 bg-sage-50 border-l-4 border-sage-500">
              <h3 className="font-semibold text-charcoal-900 mb-2">What It Doesn't Do</h3>
              <p className="text-charcoal-700">
                Refresh does <strong>not</strong> run synthesis, validation, or any operations. 
                It only fetches the current state from the server. To actually perform operations, 
                use the buttons in the respective tabs (Synthesis, Validation, Staging).
              </p>
            </div>
          </div>
        </section>

        {/* Tab Order and Purpose */}
        <section className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Tab Order and Purpose</h2>
          <p className="text-charcoal-700 mb-6">
            The tabs are organized in a logical workflow order, from monitoring to operations:
          </p>
          
          <div className="space-y-6">
            {/* Overview Tab */}
            <div className="p-6 bg-[var(--surface)] border-l-4 border-[var(--slate)]">
              <div className="flex items-center gap-3 mb-3">
                <BarChart3 className="h-5 w-5 text-[var(--text-ter)]" />
                <h3 className="text-xl font-semibold text-charcoal-900">1. Overview</h3>
              </div>
              <p className="text-charcoal-700 mb-3">
                <strong>Purpose:</strong> Start here to see the current state of your Canon system.
              </p>
              <div className="space-y-2 text-charcoal-700">
                <p><strong>Shows:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>System health metrics (sections count, validation status, dependencies)</li>
                  <li>Recent synthesis activity</li>
                  <li>Quick status indicators</li>
                  <li>Summary of validation errors and warnings</li>
                </ul>
              </div>
              <p className="text-charcoal-600 text-sm mt-3 italic">
                This is your "dashboard" - a high-level view before diving into specific operations.
              </p>
            </div>

            {/* Synthesis Tab */}
            <div className="p-6 bg-[var(--surface)] border-l-4 border-[var(--slate)]">
              <div className="flex items-center gap-3 mb-3">
                <Cog className="h-5 w-5 text-[var(--text-ter)]" />
                <h3 className="text-xl font-semibold text-charcoal-900">2. Synthesis</h3>
              </div>
              <p className="text-charcoal-700 mb-3">
                <strong>Purpose:</strong> Generate canonical documentation from source files.
              </p>
              <div className="space-y-2 text-charcoal-700">
                <p><strong>Operations:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li><strong>Run Synthesis:</strong> Discovers sources, classifies them, merges content, and generates canonical sections</li>
                  <li><strong>View Sources:</strong> See all source files that feed into the Canon</li>
                  <li><strong>View Sections:</strong> Browse generated canonical sections</li>
                </ul>
              </div>
              <div className="mt-3 p-3 bg-[var(--mustard-dim)] rounded border border-[var(--mustard)]">
                <p className="text-sm text-[var(--mustard)]">
                  <strong>⚠️ Warning:</strong> Direct synthesis writes to production. Use Staging tab for safe review workflow.
                </p>
              </div>
            </div>

            {/* Validation Tab */}
            <div className="p-6 bg-[var(--surface)] border-l-4 border-[var(--green)]">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle className="h-5 w-5 text-[var(--green)]" />
                <h3 className="text-xl font-semibold text-charcoal-900">3. Validation</h3>
              </div>
              <p className="text-charcoal-700 mb-3">
                <strong>Purpose:</strong> Check Canon structure, dependencies, and consistency.
              </p>
              <div className="space-y-2 text-charcoal-700">
                <p><strong>Checks:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li><strong>Schema Validation:</strong> Ensures all sections have required fields and valid values</li>
                  <li><strong>Dependency Validation:</strong> Verifies no circular dependencies or broken references</li>
                  <li><strong>Content Quality:</strong> Checks for completeness and consistency</li>
                  <li><strong>Lineage:</strong> Validates source tracking information</li>
                </ul>
              </div>
              <p className="text-charcoal-600 text-sm mt-3">
                Run validation after synthesis or before promoting to production to catch issues early.
              </p>
            </div>

            {/* Staging Tab */}
            <div className="p-6 bg-[var(--surface)] border-l-4 border-[var(--mustard)]">
              <div className="flex items-center gap-3 mb-3">
                <Workflow className="h-5 w-5 text-[var(--mustard)]" />
                <h3 className="text-xl font-semibold text-charcoal-900">4. Staging</h3>
              </div>
              <p className="text-charcoal-700 mb-3">
                <strong>Purpose:</strong> Safe workflow for reviewing changes before promoting to production.
              </p>
              <div className="space-y-3 text-charcoal-700">
                <div>
                  <p className="font-semibold mb-2">Operations:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li><strong>Create Staging:</strong> Runs synthesis to a staging environment (doesn't touch production)</li>
                    <li><strong>Promote to Production:</strong> After review, promotes staging changes to production</li>
                  </ul>
                </div>
                <div className="p-3 bg-[var(--green-dim)] rounded border border-[var(--green)]">
                  <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    <strong>✅ Recommended:</strong> Always use staging workflow for safety. Review changelog before promoting.
                  </p>
                </div>
                <div>
                  <p className="font-semibold mb-2">Changelog Features:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Shows what changed (new, modified, deleted sections)</li>
                    <li>Highlights definition changes and namespace collisions</li>
                    <li>Side-by-side diff view (Beyond Compare style) for modified content</li>
                    <li>Requires explicit approval before promotion</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Dependencies Tab */}
            <div className="p-6 bg-[var(--surface)] border-l-4 border-[var(--slate)]">
              <div className="flex items-center gap-3 mb-3">
                <GitBranch className="h-5 w-5 text-[var(--text-ter)]" />
                <h3 className="text-xl font-semibold text-charcoal-900">5. Dependencies</h3>
              </div>
              <p className="text-charcoal-700 mb-3">
                <strong>Purpose:</strong> Visualize and understand relationships between Canon sections.
              </p>
              <div className="space-y-2 text-charcoal-700">
                <p><strong>Shows:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Dependency graph visualization</li>
                  <li>Section relationships (depends_on, precedes, related)</li>
                  <li>Circular dependency detection</li>
                  <li>Topological ordering of sections</li>
                </ul>
              </div>
              <p className="text-charcoal-600 text-sm mt-3">
                Useful for understanding how sections relate and ensuring proper ordering.
              </p>
            </div>

            {/* Lab Tab */}
            <div className="p-6 bg-[var(--surface)] border-l-4 border-[var(--rule)]">
              <div className="flex items-center gap-3 mb-3">
                <Settings className="h-5 w-5 text-[var(--text-sec)]" />
                <h3 className="text-xl font-semibold text-charcoal-900">6. Lab</h3>
              </div>
              <p className="text-charcoal-700 mb-3">
                <strong>Purpose:</strong> Experimental features and additional tools.
              </p>
              <div className="space-y-2 text-charcoal-700">
                <p><strong>Features:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Documentation browser</li>
                  <li>System architecture explorer</li>
                  <li>Experimental tools and features</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Common Operations */}
        <section className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Common Operations</h2>
          
          <div className="space-y-6">
            {/* Staging Workflow */}
            <div className="p-6 bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200">
              <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Recommended Workflow: Staging → Review → Promote</h3>
              <ol className="space-y-3 text-charcoal-700">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-consulting-slate-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  <div>
                    <strong>Go to Staging tab</strong>
                    <p className="text-sm text-charcoal-600 mt-1">Click "Create Staging" to run synthesis in a safe environment</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-consulting-slate-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  <div>
                    <strong>Review Changelog</strong>
                    <p className="text-sm text-charcoal-600 mt-1">Check what changed: new sections, modifications, definition changes, collisions</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-consulting-slate-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  <div>
                    <strong>Review Diffs</strong>
                    <p className="text-sm text-charcoal-600 mt-1">Click "Show Diff" on modified sections to see side-by-side changes</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-consulting-slate-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                  <div>
                    <strong>Address Issues</strong>
                    <p className="text-sm text-charcoal-600 mt-1">Review definition changes, collisions, and term usage issues. Accept or dismiss as needed</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-consulting-slate-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
                  <div>
                    <strong>Promote to Production</strong>
                    <p className="text-sm text-charcoal-600 mt-1">Click "Promote to Production" and confirm. This moves staging changes to production</p>
                  </div>
                </li>
              </ol>
            </div>

            {/* Button Explanations */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-charcoal-900">Button Reference</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-[var(--bg-raised)] border border-[var(--rule)]">
                  <div className="flex items-center gap-2 mb-2">
                    <Play className="h-4 w-4 text-[var(--green)]" />
                    <strong className="text-charcoal-900">Run Synthesis / Create Staging</strong>
                  </div>
                  <p className="text-sm text-charcoal-700">
                    Starts the synthesis process. In Staging tab, creates a staging version. In Synthesis tab, writes directly to production.
                  </p>
                </div>

                <div className="p-4 bg-[var(--bg-raised)] border border-[var(--rule)]">
                  <div className="flex items-center gap-2 mb-2">
                    <Download className="h-4 w-4 text-[var(--text-ter)]" />
                    <strong className="text-charcoal-900">Promote to Production</strong>
                  </div>
                  <p className="text-sm text-charcoal-700">
                    Moves staging changes to production. Requires explicit confirmation and shows approval dialog with changelog summary.
                  </p>
                </div>

                <div className="p-4 bg-[var(--bg-raised)] border border-[var(--rule)]">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="h-4 w-4 text-[var(--text-ter)]" />
                    <strong className="text-charcoal-900">Show Diff</strong>
                  </div>
                  <p className="text-sm text-charcoal-700">
                    Expands to show side-by-side comparison (Beyond Compare style) of before/after content for modified sections.
                  </p>
                </div>

                <div className="p-4 bg-[var(--bg-raised)] border border-[var(--rule)]">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-4 w-4 text-[var(--green)]" />
                    <strong className="text-charcoal-900">Accept & Add to Glossary</strong>
                  </div>
                  <p className="text-sm text-charcoal-700">
                    Accepts a term usage or definition and adds it to the glossary as the canonical definition.
                  </p>
                </div>

                <div className="p-4 bg-[var(--bg-raised)] border border-[var(--rule)]">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-[var(--text-ter)]" />
                    <strong className="text-charcoal-900">Accept as 2nd Definition</strong>
                  </div>
                  <p className="text-sm text-charcoal-700">
                    Accepts an alternate definition for a term (polysemy support), allowing multiple valid meanings.
                  </p>
                </div>

                <div className="p-4 bg-[var(--bg-raised)] border border-[var(--rule)]">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-[var(--mustard)]" />
                    <strong className="text-charcoal-900">Dismiss</strong>
                  </div>
                  <p className="text-sm text-charcoal-700">
                    Marks an issue as dismissed/not a problem. Useful for false positives or intentional variations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Understanding the Changelog */}
        <section className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Understanding the Changelog</h2>
          
          <div className="space-y-4">
            <div className="p-4 bg-[var(--slate-dim)] border-l-4 border-[var(--slate)]">
              <h3 className="font-semibold text-charcoal-900 mb-2">Summary Cards</h3>
              <p className="text-charcoal-700 mb-2">At the top, you'll see cards showing:</p>
              <ul className="list-disc list-inside ml-4 space-y-1 text-charcoal-700">
                <li><strong>New Sections:</strong> Sections that didn't exist before</li>
                <li><strong>Modified Sections:</strong> Sections with changes (content, frontmatter, or both)</li>
                <li><strong>Deleted Sections:</strong> Sections removed from Canon</li>
                <li><strong>Definition Changes:</strong> Terms used differently or defined differently</li>
                <li><strong>Dependency Changes:</strong> Changes to section relationships</li>
              </ul>
            </div>

            <div className="p-4 bg-[var(--coral-dim)] border-l-4 border-[var(--coral)]">
              <h3 className="font-semibold text-charcoal-900 mb-2">Critical Warnings</h3>
              <p className="text-charcoal-700 mb-2">
                Definition changes are marked with a red warning banner because they indicate:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1 text-charcoal-700">
                <li><strong>Wrong Usage:</strong> Term used incorrectly according to glossary</li>
                <li><strong>Definition Needs Update:</strong> Usage suggests definition should change</li>
                <li><strong>Collisions:</strong> Same term used with different meanings in multiple places</li>
              </ul>
              <p className="text-sm text-[var(--coral)] mt-2 font-semibold">
                Always review these carefully before promoting!
              </p>
            </div>

            <div className="p-4 bg-sage-50 border-l-4 border-sage-500">
              <h3 className="font-semibold text-charcoal-900 mb-2">Diff View</h3>
              <p className="text-charcoal-700 mb-2">
                Click "Show Diff" on any modified section to see:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1 text-charcoal-700">
                <li><strong>Left side (BEFORE):</strong> Original content with removed lines in red</li>
                <li><strong>Right side (AFTER):</strong> New content with added lines in green</li>
                <li><strong>Line numbers:</strong> Reference for where changes occurred</li>
                <li><strong>Color coding:</strong> Red = removed, Green = added, Yellow = modified</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Tips and Best Practices */}
        <section className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Tips and Best Practices</h2>
          
          <div className="space-y-4">
            <div className="p-4 bg-[var(--green-dim)] border-l-4 border-[var(--green)]">
              <h3 className="font-semibold text-charcoal-900 mb-2">✅ Do</h3>
              <ul className="list-disc list-inside space-y-1 text-charcoal-700">
                <li>Always use the Staging workflow for changes</li>
                <li>Review the changelog before promoting</li>
                <li>Check definition changes and collisions carefully</li>
                <li>Use "Show Diff" to understand what actually changed</li>
                <li>Refresh after operations to see updated state</li>
                <li>Run validation before promoting to catch issues</li>
              </ul>
            </div>

            <div className="p-4 bg-[var(--coral-dim)] border-l-4 border-[var(--coral)]">
              <h3 className="font-semibold text-charcoal-900 mb-2">❌ Don't</h3>
              <ul className="list-disc list-inside space-y-1 text-charcoal-700">
                <li>Don't use direct Synthesis tab unless you're certain about changes</li>
                <li>Don't promote without reviewing the changelog</li>
                <li>Don't ignore definition change warnings</li>
                <li>Don't edit docs_prod/ directly (it's generated, not source)</li>
                <li>Don't skip validation before production promotion</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
          <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Related Documentation</h3>
          <div className="flex flex-wrap gap-3">
            <Link 
              to="/elevation/canon-engine" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-50 text-consulting-slate-blue-700 hover:bg-consulting-slate-blue-100 transition-colors"
            >
              <Cog className="h-4 w-4" />
              Canon Engine Dashboard
            </Link>
            <Link 
              to="/elevation/definition/canon-engine" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--surface)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <BookOpen className="h-4 w-4" />
              Canon Engine Definition
            </Link>
            <a 
              href="/wiki/CANON_SYSTEM_GUIDE.md" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--surface)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileText className="h-4 w-4" />
              Canon System Guide
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};









